import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatCircleTextIcon } from '@phosphor-icons/react/dist/csr/ChatCircleText';
import { CardHead } from '../../design-system/composites/Card/Card';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { PaperPlaneTiltIcon } from '@phosphor-icons/react/dist/csr/PaperPlaneTilt';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowsClockwise';

import { Button } from '../../design-system/primitives/Button/Button';
import { SendMessageDialog } from './SendMessageDialog';
import { ResendCaseDialog } from './ResendCaseDialog';
import { smsApi, type SmsMessageRow, type SmsTemplate } from '../../lib/api/sms';
import { formatTimestamp } from '../../lib/format/dateTime';
import styles from './CaseMessages.module.css';

/**
 * The Messages panel on the case detail screen (M-08, Task 8) — what was
 * sent about this case, and the control to send one of the seeded
 * templates. Mounted below `CaseDetail`'s own `<dl>` of fields, exactly the
 * way `CaseUpdateDialog`/the close `Dialog` sit alongside it: one more
 * case-scoped surface, gated on its own permission.
 *
 * ## `canSend` is a prop, not a re-derived check
 *
 * `CaseDetail` already computes `permissions.includes('sms.send')` the
 * identical way it computes `canUpdate`/`canClose` for its own two
 * controls — this component takes the boolean rather than the raw
 * permission list, so there is exactly one place in the tree that reads
 * `'sms.send'` as a string literal.
 *
 * ## A `queued` message is rendered as queued, not spun forever (Task 8
 * ruling 1)
 *
 * `SendSmsMessage` is a queued job; this screen has no socket and no
 * polling loop back to `GET /sms-messages/{message}` here (Task 8 is the
 * panel and the log, not a live-update mechanism) — a message sent this
 * session shows `queued` until the next time this panel loads (a
 * navigation away and back, or a manual reload), which is the honest
 * state of a message still sitting in `jobs`. A spinner that never
 * resolves would claim a certainty about delivery this screen cannot
 * actually have.
 *
 * ## The log fetch and the template catalogue fetch are independent
 *
 * Both run on mount, side by side — a session that only ever reads this
 * case's history (no `sms.send`) still gets a working log with no wasted
 * request; a session that also sends still has the picker's options
 * ready by the time the dialog opens, rather than fetching them for the
 * first time on the dialog's own mount.
 *
 * ## A rejected log fetch answers an error state, not an endless spinner
 * (final whole-branch review, Minor 2)
 *
 * `loadMessages()` used to be `void`-called with no `catch` — a rejected
 * `smsApi.list()` left `messages` at its initial `null` forever, which
 * renders `t('list.loading')` for the rest of the session: the "spinner
 * that never ends" the module's own ruling above (`CaseDetail` gets one)
 * rules out for the record but did not, until this fix, also rule out for
 * ITS OWN log. `status` mirrors `CaseDetail`'s own `LoadStatus` shape
 * (`'loading' | 'ready' | 'error'`) rather than inventing a second
 * vocabulary for the same three states, with a retry control the same
 * shape as `CaseDetail`'s.
 */

export interface CaseMessagesProps {
  caseUuid: string;
  canSend: boolean;
  /** Offer "Resend case" — `sms.send` on a case that is still open. */
  canResend?: boolean;
}

/** Reuses the six case-status tokens (`tokens.css`) rather than inventing a
 *  seventh palette for a five-value vocabulary that shares none of their
 *  names — see `CaseMessages.module.css` for which token backs which status. */
const STATUS_CLASS: Record<SmsMessageRow['status'], string> = {
  queued: 'statusQueued',
  sending: 'statusSending',
  sent: 'statusSent',
  failed: 'statusFailed',
  rejected: 'statusRejected',
};

type LoadStatus = 'loading' | 'ready' | 'error';

export function CaseMessages({ caseUuid, canSend, canResend = false }: CaseMessagesProps) {
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [messages, setMessages] = useState<readonly SmsMessageRow[] | null>(null);
  const [templates, setTemplates] = useState<readonly SmsTemplate[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);

  const loadMessages = useCallback(async () => {
    setStatus('loading');

    try {
      const rows = await smsApi.list(caseUuid);
      setMessages(rows);
      setStatus('ready');
    } catch {
      // The panel's own error state, not `CaseDetail`'s — a message-log
      // failure must not blank the whole case screen out from under the
      // fields/edit/close controls that already rendered successfully.
      setStatus('error');
    }
  }, [caseUuid]);

  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  // Independent of the log fetch above — see this module's own docblock.
  useEffect(() => {
    if (!canSend) return;
    void smsApi.templates().then(setTemplates);
  }, [canSend]);

  return (
    <section className={styles['panel']}>
      <CardHead
        level="h3"
        icon={<ChatCircleTextIcon size={16} weight="duotone" />}
        title={t('sms.title')}
        aside={
          canSend ? (
            <div className={styles['headActions']}>
              {canResend ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setResendOpen(true);
                  }}
                  icon={<ArrowsClockwiseIcon size={16} />}
                >
                  {t('sms.resendTitle')}
                </Button>
              ) : null}
              <Button
                variant="secondary"
                onClick={() => {
                  setDialogOpen(true);
                }}
                icon={<PaperPlaneTiltIcon size={16} />}
              >
                {t('sms.sendTitle')}
              </Button>
            </div>
          ) : undefined
        }
      />

      {status === 'error' ? (
        <div role="alert" className={styles['empty']}>
          <p>{t('sms.loadFailed')}</p>
          <Button
            variant="secondary"
            onClick={() => {
              void loadMessages();
            }}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('cases.retry')}
          </Button>
        </div>
      ) : messages === null ? (
        <p role="status">{t('list.loading')}</p>
      ) : messages.length === 0 ? (
        <p className={styles['empty']}>{t('sms.noMessages')}</p>
      ) : (
        <ul className={styles['list']}>
          {messages.map((message) => (
            <li key={message.uuid} className={styles['row']}>
              <div className={styles['rowHeader']}>
                <span className={styles['templateName']}>
                  {message.template?.name ?? message.template?.code ?? ''}
                </span>
                <span className={[styles['statusPill'], styles[STATUS_CLASS[message.status]]].join(' ')}>
                  {t(`sms.status.${message.status}`)}
                </span>
              </div>

              <p className={styles['body']} dir="auto">
                <span className={styles['bodyLabel']}>{t('sms.body')}</span>
                {message.rendered_body}
              </p>

              {message.failure_reason === null ? null : (
                <p className={styles['failure']} role="alert">
                  {message.failure_reason}
                </p>
              )}

              <div className={styles['meta']}>
                <bdi dir="ltr">{formatTimestamp(message.queued_at, i18n.language)}</bdi>
                {message.sent_at === null ? null : (
                  <bdi dir="ltr">{formatTimestamp(message.sent_at, i18n.language)}</bdi>
                )}
                {message.failed_at === null ? null : (
                  <bdi dir="ltr">{formatTimestamp(message.failed_at, i18n.language)}</bdi>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <SendMessageDialog
        open={dialogOpen}
        caseUuid={caseUuid}
        templates={templates}
        onClose={() => {
          setDialogOpen(false);
        }}
        onSent={(row) => {
          // Task 8 ruling 3 — prepend the row the API already answered
          // with; refetching would be a second request answering the
          // same question `POST` just answered.
          setMessages((current) => [row, ...(current ?? [])]);
          setDialogOpen(false);
        }}
      />

      <ResendCaseDialog
        open={resendOpen}
        caseUuid={caseUuid}
        onClose={() => {
          setResendOpen(false);
        }}
        onSent={(row) => {
          setMessages((current) => [row, ...(current ?? [])]);
          setResendOpen(false);
        }}
      />
    </section>
  );
}
